/// <reference path="report-validity.d.ts"/>
import * as Sentry from "@sentry/browser";
import React, { FormEvent, MouseEvent, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import reportValidity from "report-validity";

import { AnswerTemplate } from "../../db/models/Answer";
import { FormInterstitial, QuestionGroup as FormQuestionGroup } from "../../db/models/Form";
import Question, { Option } from "../../db/models/Question";
import { parseInputValues } from "../controllers/answer-parsing";
import Interstitial from "./Interstitial";
import Progress from "./Progress";
import QuestionGroup from "./QuestionGroup";
import ReportFormMain from "./ReportFormMain";
import ReviewInterstitial from "./ReviewInterstitial";
import sendBeacon from "./beacon";
import { nextPage } from "./page-logic";

function fileToBase64(file: File): Promise<string> {
  const fileReader = new FileReader();
  return new Promise((resolve, reject) => {
    fileReader.onerror = () => {
      fileReader.abort();
      reject(new DOMException("Problem parsing input file."));
    };

    fileReader.onload = () => {
      resolve(fileReader.result as string);
    };

    fileReader.readAsDataURL(file);
  });
}

interface ReportFormProps {
  formId: string;
  csrfToken: string;
  pages: (FormQuestionGroup | FormInterstitial)[];
  pageHistory: number[]; // pages are 1-indexed
  answers: Record<string, AnswerTemplate[]>;
  sessionId: string;
}

interface HistoryPage {
  pageHistory: number[];
}

export interface InvalidQuestionWarning {
  question: Question;
  option?: Option;
}

function equal(a: InvalidQuestionWarning, b: InvalidQuestionWarning) {
  return a.question === b.question && a.option === b.option;
}

export default function ReportForm({
  pages,
  pageHistory: originalPageHistory,
  formId,
  csrfToken,
  sessionId,
  ...props
}: ReportFormProps): ReactElement {
  const [answers, setAnswers] = useState(props.answers);
  const [showValidationError, setShowValidationError] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const scrollRef = useRef<HTMLElement>(null);
  const [fullPageHistory, setFullPageHistory] = useState(originalPageHistory);
  const currentPage = fullPageHistory[0];
  const canGoBack = fullPageHistory.length > originalPageHistory.length;
  const page = pages[currentPage - 1];
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === pages.length; // The last page cannot be conditional so we don't have to check if there are hidden pages past this point.
  const [loading, setLoading] = useState(false);
  const [invalidQuestions, setInvalidQuestions] = useState<Array<InvalidQuestionWarning>>([]);

  const handleOnInvalidQuestion = useCallback(
    (warning: InvalidQuestionWarning) => {
      if (!invalidQuestions.some((other) => equal(warning, other))) {
        setInvalidQuestions([...invalidQuestions, warning]);
      }
    },
    [invalidQuestions],
  );

  const handleOnValidQuestion = useCallback(
    (warning: InvalidQuestionWarning) => {
      if (invalidQuestions.some((other) => equal(warning, other))) {
        setInvalidQuestions([...invalidQuestions.filter((other) => !equal(warning, other))]);
      }
    },
    [invalidQuestions],
  );

  useEffect(() => {
    Sentry.addBreadcrumb({ category: "reportFormLoaded", data: { pageHistory: originalPageHistory } });
  }, [originalPageHistory]);

  // Don't set fullPageHistory directly, use this wrapped function which also updates the hit counter etc
  const changePage = useCallback(
    async (page: HistoryPage | undefined, focusQuestion?: string) => {
      Sentry.addBreadcrumb({ category: "changePage", data: page });
      setShowValidationError(false);
      const pageHistory = page?.pageHistory ?? originalPageHistory;
      sendBeacon("FORM", formId, pageHistory[0], currentPage, sessionId);
      setFullPageHistory(pageHistory);
      if (focusQuestion) {
        setTimeout(() => {
          document.getElementById(`${focusQuestion}-container`)?.scrollIntoView();
        }, 0);
      } else {
        scrollRef.current!.scrollIntoView();
      }
    },
    [currentPage, formId, originalPageHistory, sessionId],
  );

  // Don't call the changePage wrapper directly either, use either this function to go forwards, or history.back() to go back:
  const advanceToPage = useCallback(
    async (pageNumber: number) => {
      Sentry.addBreadcrumb({ category: "advancePage", data: { pageNumber } });
      const page: HistoryPage = { pageHistory: [pageNumber, ...fullPageHistory] };
      window.history.pushState(page, "", `#step-${pageNumber}`);
      await changePage(page);
    },
    [changePage, fullPageHistory],
  );

  useEffect(() => {
    const onPopState = (ev: PopStateEvent) => changePage(ev.state);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [changePage, fullPageHistory]);

  // This combines the current answers with the answers from previous pages and returns a big "answers" object. It has to be async to handle file inputs.
  const updateAnswersFromForm = useCallback(async () => {
    if (page.type !== "QUESTION_GROUP") return answers;
    const elements = Array.from(formRef.current!.elements) as (HTMLInputElement | HTMLTextAreaElement)[];
    // Get an element containing the value of every input in the form — including all the question inputs, but also the hidden inputs for "_crsf", "previous-answers", etc
    const inputValues: Record<string, Array<string | Promise<string>>> = {};
    for (const element of elements) {
      if (!element?.name || element.type === "button") continue;
      const { name, type, value, files, checked } = element as HTMLInputElement;
      if ((type === "checkbox" || type === "radio") && !checked) continue;
      if (!inputValues[name]) inputValues[name] = [];
      if (files) {
        for (const file of files) {
          // fileToBase64(file) returns a promise but for speed we just collect all the promises here and then resolve them in parseInputValues
          inputValues[name].push(fileToBase64(file));
        }
      } else {
        inputValues[name].push(value);
      }
    }
    return await parseInputValues(page.questions, inputValues);
  }, [answers, page]);

  // This can be:
  //   • undefined — do not show a "back" button
  //   • a no-op function — show a "back" button but allow it to submit the form as normal
  //   • an active function — show a "back" button and trigger history.back() on click
  const onBack = useMemo(
    () =>
      isFirstPage
        ? undefined
        : (ev: MouseEvent<HTMLButtonElement>) => {
            Sentry.addBreadcrumb({ category: "clickedBack", data: { canGoBack } });
            if (canGoBack) {
              ev.preventDefault();
              window.history.back();
            } else {
              // @ts-ignore — This is a hack to prevent the form's onSubmit handler from firing if you click "back" but we can't handle it client-side. This only happens if JS loads partway through a form (say because of a flaky connection). A better solution would be to use SubmitEvent.submitter, but that's not supported by IE or TypeScript, so that solution will have to wait.
              window.formEventHandled = true;
            }
          },
    [canGoBack, isFirstPage],
  );

  const onNext = useCallback(
    async (ev: FormEvent<HTMLFormElement>) => {
      // @ts-ignore (see onBack for explanation)
      if (window.formEventHandled) {
        // This form submission was triggered by the "back" or "submit now" button so we shouldn't handle it here.
        return;
      }
      if (isLastPage && !loading) {
        // On the last page, we should let the browser's in built behaviour submit the form.
        // Don't call ev.preventDefault().
        // Set this state to true to display the "submitting" spinner,
        // which does nothing except disable the buttons and make the user feel like something is happening.
        setLoading(true);
        return;
      }
      ev.preventDefault();
      if (loading) {
        return;
      }
      setInvalidQuestions([]);
      const form = ev.currentTarget;
      const requiredChoiceInputsOnThisPage = (page.type === "QUESTION_GROUP" ? page.questions : []).filter(
        (question) => question.required && (question.questionType === "RADIO" || question.questionType === "CHECKLIST"),
      );
      const answers = await updateAnswersFromForm();

      if (
        reportValidity(form) &&
        requiredChoiceInputsOnThisPage.every((question) => question.id in answers && answers[question.id].length > 0)
      ) {
        setAnswers(answers);
        await advanceToPage(nextPage(currentPage, pages, answers));
      } else {
        setShowValidationError(true);
        window.scrollTo(0, 0);
      }
    },
    [isLastPage, loading, page, updateAnswersFromForm, currentPage, pages, advanceToPage],
  );

  // Returns a wrapped "goToPage" function if there's a final interstitial.
  // Otherwise allows the regular HTML behaviour will submit the form.
  const onSkipToEnd = useCallback(
    async (ev: MouseEvent<HTMLButtonElement>) => {
      const formHasInterstitial = pages[pages.length - 1].type === "INTERSTITIAL";
      Sentry.addBreadcrumb({
        category: "clickedSkipToEnd",
        data: { formHasInterstitial },
      });
      if (formHasInterstitial) {
        ev.preventDefault();
        await advanceToPage(pages.length);
      } else {
        // @ts-ignore Set the same flag from onBack, so that the onSubmit handler doesn't override the form's default submit behaviour and take us to the next page.
        window.formEventHandled = true;
      }
    },
    [pages, advanceToPage],
  );

  // Used to skip between pages (E.g., for change functionality)
  const onChangePage = useCallback(
    async (pageNumber: number, focusQuestion?: string) => {
      const page: HistoryPage = {
        pageHistory: fullPageHistory.filter((historicalPage) => historicalPage <= pageNumber),
      };
      window.history.pushState(page, "", `#step-${pageNumber}`);
      await changePage(page, focusQuestion);
    },
    [changePage, fullPageHistory],
  );

  return (
    <>
      <Progress currentPage={currentPage} totalPages={pages.length} scrollRef={scrollRef} />
      <ReportFormMain
        onSubmit={onNext}
        csrfToken={csrfToken}
        answers={answers}
        pageHistory={fullPageHistory}
        formRef={formRef}
        loading={loading}
        sessionId={sessionId}
      >
        {page.type === "QUESTION_GROUP" ? (
          <QuestionGroup
            key={currentPage}
            page={page}
            isLastPage={isLastPage}
            loading={loading}
            answers={answers}
            onBack={onBack}
            onSkipToEnd={onSkipToEnd}
            showValidationError={showValidationError}
            onInvalidQuestion={handleOnInvalidQuestion}
            onValidQuestion={handleOnValidQuestion}
            invalidQuestions={invalidQuestions}
          />
        ) : page.type === "INTERSTITIAL" && !page.showReview ? (
          <Interstitial
            key={currentPage}
            page={page}
            isLastPage={isLastPage}
            loading={loading}
            onBack={onBack}
            onSkipToEnd={onSkipToEnd}
            showValidationError={showValidationError}
          />
        ) : page.type === "INTERSTITIAL" && page.showReview ? (
          <ReviewInterstitial
            key={currentPage}
            page={page}
            isLastPage={isLastPage}
            loading={loading}
            onBack={onBack}
            onSkipToEnd={onSkipToEnd}
            onChangePage={onChangePage}
            showValidationError={showValidationError}
            pages={pages}
            answers={answers}
          />
        ) : null}
      </ReportFormMain>
    </>
  );
}
